import { useEffect, useState } from "react";
import { adminApi, formatAud } from "../../lib/api";
import { toast } from "sonner";
import { Link2, Copy, CheckCheck, Plus, X, ExternalLink } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU", {
  day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit"
}) : "—";

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    amount: "", description: "BCP Payment",
    customer_name: "", customer_email: "",
  });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    if (!form.description.trim()) { toast.error("Description required"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/payment-links", {
        amount: Number(form.amount),
        description: form.description.trim(),
        customer_name: form.customer_name.trim() || null,
        customer_email: form.customer_email.trim() || null,
      });
      setCreated(data);
      onCreated();
      toast.success("Payment link created!");
    } catch(e) {
      toast.error(e?.response?.data?.detail || "Failed to create link");
    } finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(created.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Link copied!");
  };

  const inp = { background:"#fff", borderColor:"#dde3ef", color:"#0f172a" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background:"rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
        style={{ border:"1px solid #dde3ef" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="p-5 flex items-center justify-between" style={{ borderBottom:"1px solid #eef2f8" }}>
          <div>
            <div className="font-heading font-bold text-[17px]" style={{ color:"#0f172a" }}>
              Generate Payment Link
            </div>
            <div className="text-[12px]" style={{ color:"#94a3b8" }}>
              No order required — just enter amount and send
            </div>
          </div>
          <button onClick={onClose}><X className="h-5 w-5" style={{ color:"#94a3b8" }}/></button>
        </div>

        <div className="p-5 space-y-4">
          {!created ? (
            <>
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>
                  Amount (AUD) *
                </Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold text-[14px]"
                    style={{ color:"#64748b" }}>$</span>
                  <Input type="number" min="0.01" step="0.01"
                    value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00" className="pl-7 rounded-xl text-[15px] font-bold" style={inp}/>
                </div>
              </div>

              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>
                  Description *
                </Label>
                <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="e.g. Deposit for Toyota Camry door" className="mt-1 rounded-xl" style={inp}/>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>
                    Customer Name
                  </Label>
                  <Input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })}
                    placeholder="Optional" className="mt-1 rounded-xl" style={inp}/>
                </div>
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>
                    Customer Email
                  </Label>
                  <Input type="email" value={form.customer_email}
                    onChange={e => setForm({ ...form, customer_email: e.target.value })}
                    placeholder="Optional" className="mt-1 rounded-xl" style={inp}/>
                </div>
              </div>

              <button onClick={submit} disabled={busy}
                className="w-full h-11 rounded-xl font-bold text-[14px] disabled:opacity-50 transition-all"
                style={{ background:"#0b1a3b", color:"#ffffff" }}>
                {busy ? "Generating…" : `Generate Link${form.amount ? ` — ${formatAud(Number(form.amount))}` : ""}`}
              </button>
            </>
          ) : (
            /* Success state */
            <div className="space-y-4">
              <div className="rounded-xl p-4 text-center" style={{ background:"#f0fdf4", border:"1px solid #86efac" }}>
                <div className="text-[28px] font-heading font-black" style={{ color:"#15803d" }}>
                  {formatAud(created.amount)}
                </div>
                <div className="text-[13px] font-semibold mt-1" style={{ color:"#15803d" }}>
                  Payment link generated ✓
                </div>
                <div className="text-[11px] mt-0.5" style={{ color:"#16a34a" }}>Ref: {created.ref}</div>
              </div>

              <div className="rounded-xl p-3" style={{ background:"#f5f7fb", border:"1px solid #dde3ef" }}>
                <div className="text-[11px] font-mono mb-1.5" style={{ color:"#64748b" }}>Payment URL</div>
                <div className="text-[11px] break-all" style={{ color:"#0f172a" }}>{created.url}</div>
              </div>

              <div className="flex gap-2">
                <button onClick={copy}
                  className="flex-1 h-10 rounded-xl font-bold text-[13px] flex items-center justify-center gap-2 transition-all"
                  style={{ background: copied ? "#15803d" : "#0b1a3b", color:"#ffffff" }}>
                  {copied ? <><CheckCheck className="h-4 w-4"/>Copied!</> : <><Copy className="h-4 w-4"/>Copy Link</>}
                </button>
                <a href={created.url} target="_blank" rel="noreferrer"
                  className="h-10 px-4 rounded-xl font-bold text-[13px] flex items-center gap-1.5"
                  style={{ background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe" }}>
                  <ExternalLink className="h-4 w-4"/>Open
                </a>
              </div>
              <button onClick={onClose}
                className="w-full h-9 rounded-xl text-[13px] font-semibold"
                style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminPaymentLinks() {
  const [items,  setItems]  = useState([]);
  const [loading,setLoading]= useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [copied, setCopied] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/payment-links");
      setItems(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const copy = (id, url) => {
    navigator.clipboard.writeText(url);
    setCopied(c => ({ ...c, [id]: true }));
    setTimeout(() => setCopied(c => ({ ...c, [id]: false })), 2000);
    toast.success("Link copied!");
  };

  return (
    <div className="space-y-4" data-testid="admin-payment-links">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color:"#94a3b8" }}>Finance</div>
          <h1 className="font-heading text-[26px] font-bold" style={{ color:"#0f172a" }}>Payment Links</h1>
          <div className="text-[13px] mt-0.5" style={{ color:"#64748b" }}>
            Generate Stripe payment links without an order — for deposits, phone payments, or custom amounts.
          </div>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="h-10 px-5 rounded-xl font-bold text-[13px] flex items-center gap-2"
          style={{ background:"#0b1a3b", color:"#ffffff" }}>
          <Plus className="h-4 w-4"/>New Payment Link
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm" style={{ border:"1px solid #dde3ef" }}>
        <table className="w-full" style={{ borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:"#f5f7fb", borderBottom:"1px solid #dde3ef" }}>
              {["Ref","Description","Customer","Amount","Created","Status","Actions"].map(h => (
                <th key={h} className="py-2.5 px-3 text-left font-mono font-semibold uppercase"
                  style={{ color:"#94a3b8", fontSize:10, letterSpacing:"0.12em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-10 text-center">
                <div className="w-5 h-5 border-2 border-[#1e5bb8] border-t-transparent rounded-full animate-spin mx-auto"/>
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="p-10 text-center font-mono uppercase tracking-widest text-[11px]"
                style={{ color:"#94a3b8" }}>No payment links yet</td></tr>
            ) : items.map((item, idx) => (
              <tr key={item.id}
                style={{ borderBottom: idx < items.length-1 ? "1px solid #f0f4f8" : "none",
                  background: idx%2===0 ? "#fff" : "#fafbfc" }}>
                <td className="px-3 py-2.5">
                  <span className="font-mono text-[12px] font-bold" style={{ color:"#0b1a3b" }}>{item.id}</span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="text-[13px] max-w-[160px] truncate" style={{ color:"#0f172a" }}>{item.description}</div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="text-[12px]" style={{ color:"#0f172a" }}>{item.customer_name || "—"}</div>
                  <div className="text-[10px] font-mono" style={{ color:"#94a3b8" }}>{item.customer_email || ""}</div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="font-heading font-bold text-[13px]" style={{ color:"#0f172a" }}>
                    {formatAud(item.amount)}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="text-[11px] font-mono" style={{ color:"#64748b" }}>{fmtDate(item.created_at)}</div>
                  <div className="text-[10px]" style={{ color:"#94a3b8" }}>{item.created_by}</div>
                </td>
                <td className="px-3 py-2.5">
                  <span className="text-[10px] font-mono font-bold uppercase px-2 py-0.5 rounded-full"
                    style={item.status === "paid"
                      ? { background:"#dcfce7", color:"#15803d" }
                      : { background:"#fef3c7", color:"#92400e" }}>
                    {item.status}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => copy(item.id, item.url)} title="Copy link"
                      className="h-7 px-2.5 rounded-lg flex items-center gap-1.5 text-[11px] font-semibold transition-all"
                      style={copied[item.id]
                        ? { background:"#dcfce7", color:"#15803d", border:"1px solid #86efac" }
                        : { background:"#f5f7fb", color:"#475569", border:"1px solid #dde3ef" }}>
                      {copied[item.id] ? <CheckCheck className="h-3 w-3"/> : <Copy className="h-3 w-3"/>}
                      {copied[item.id] ? "Copied" : "Copy"}
                    </button>
                    <a href={item.url} target="_blank" rel="noreferrer"
                      className="h-7 w-7 rounded-lg flex items-center justify-center transition-all"
                      style={{ background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe" }}>
                      <ExternalLink className="h-3.5 w-3.5"/>
                    </a>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateModal onClose={() => setShowCreate(false)} onCreated={load}/>
      )}
    </div>
  );
}
